import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import NewAnalysis from "./pages/NewAnalysis";
import AnalysisDetail from "./pages/AnalysisDetail";
import ValidationLab from "./pages/ValidationLab";
import KPIDashboard from "./pages/KPIDashboard";
import DealComparator from "./pages/DealComparator";
import RadarDashboard from "./pages/RadarDashboard";
import Intro from "./pages/Intro";
import Archive from "./pages/Archive";
import Account from "./pages/Account";
import ChangePlan from "./pages/ChangePlan";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import Terms from "./pages/legal/Terms";
import CookiePolicy from "./pages/legal/CookiePolicy";
import AIDisclaimer from "./pages/legal/AIDisclaimer";
import Contact from "./pages/Contact";
import GdprRequests from "./pages/admin/GdprRequests";
import CoverageAudit from "./pages/admin/CoverageAudit";
import ObservationGrowth from "./pages/admin/ObservationGrowth";
import DataQuality from "./pages/admin/DataQuality";
import AdminHome from "./pages/admin/AdminHome";
import AdminSubscribers from "./pages/admin/Subscribers";
import AiCosts from "./pages/admin/AiCosts";
import Traffic from "./pages/admin/Traffic";
import Funnel from "./pages/admin/Funnel";
import CookieConsent from "./components/CookieConsent";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { useAuth } from "./context/AuthContext";
import api from "./lib/api";

function ThemedToaster() {
    const { resolvedTheme } = useTheme();
    return <Toaster position="bottom-right" theme={resolvedTheme} />;
}

function PixelTracker() {
    const location = useLocation();
    const first = useRef(true);
    useEffect(() => {
        if (first.current) { first.current = false; return; }
        if (window.fbq) window.fbq("track", "PageView");
    }, [location.pathname]);
    return null;
}

function TrafficTracker() {
    const location = useLocation();
    const { user } = useAuth();
    const lastPath = useRef(null);
    useEffect(() => {
        if (user === null) return;
        if (user && user.role === "admin") return;
        if (location.pathname.startsWith("/admin")) return;
        if (lastPath.current === location.pathname) return;
        lastPath.current = location.pathname;
        let sid = sessionStorage.getItem("ai_sid");
        const isNew = !sid;
        if (isNew) {
            sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            sessionStorage.setItem("ai_sid", sid);
        }
        api.post("/track/pv", {
            path: location.pathname,
            session_id: sid,
            referrer: isNew ? document.referrer || "" : "",
        }).catch(() => {});
    }, [location.pathname, user]);
    return null;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ThemeProvider>
                    <PixelTracker />
                    <TrafficTracker />
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/new"
                            element={
                                <ProtectedRoute>
                                    <NewAnalysis />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/auctions/:id"
                            element={
                                <ProtectedRoute>
                                    <AnalysisDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/validation"
                            element={
                                <ProtectedRoute>
                                    <ValidationLab />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/kpi"
                            element={
                                <ProtectedRoute>
                                    <KPIDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/archive"
                            element={
                                <ProtectedRoute>
                                    <Archive />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/compare"
                            element={
                                <ProtectedRoute>
                                    <DealComparator />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/radar"
                            element={
                                <ProtectedRoute>
                                    <RadarDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/account"
                            element={
                                <ProtectedRoute>
                                    <Account />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/piano"
                            element={
                                <ProtectedRoute>
                                    <ChangePlan />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/payment/success"
                            element={
                                <ProtectedRoute>
                                    <PaymentSuccess />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/payment/cancel"
                            element={
                                <ProtectedRoute>
                                    <PaymentCancel />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute>
                                    <AdminHome />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/utenti"
                            element={
                                <ProtectedRoute>
                                    <AdminSubscribers />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/richieste"
                            element={
                                <ProtectedRoute>
                                    <GdprRequests />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/copertura"
                            element={
                                <ProtectedRoute>
                                    <CoverageAudit />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/crescita"
                            element={
                                <ProtectedRoute>
                                    <ObservationGrowth />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/funnel"
                            element={
                                <ProtectedRoute>
                                    <Funnel />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/traffico"
                            element={
                                <ProtectedRoute>
                                    <Traffic />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/costi"
                            element={
                                <ProtectedRoute>
                                    <AiCosts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/data-quality"
                            element={
                                <ProtectedRoute>
                                    <DataQuality />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/termini" element={<Terms />} />
                        <Route path="/cookie" element={<CookiePolicy />} />
                        <Route path="/disclaimer-ai" element={<AIDisclaimer />} />
                        <Route path="/contatti" element={<Contact />} />
                        <Route path="/" element={<Intro />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    <CookieConsent />
                    <ThemedToaster />
                    </ThemeProvider>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
